import type { FilterCondition } from "../../../../../../../redux/services/helpdeskApi";
import type { HelpdeskViewItem } from "../../AllTickets/utils/types";
import type { HelpdeskCustomFieldDefinition } from "../../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";
import {
  NATIVE_ARRAY_FIELDS,
  NATIVE_STRING_FIELDS,
  DURATION_OPTIONS,
  getDurationUTCValue,
  getDefaultOperatorForFieldType,
  createEmptyCondition,
  createEmptyGroup,
  type FilterConditionUI,
  type FilterGroupUI,
  type ViewFormValues,
} from "./constants";

const ALL_NATIVE_FIELDS = [
  ...NATIVE_ARRAY_FIELDS,
  ...NATIVE_STRING_FIELDS,
  "request_support",
  "duration",
];

function isApproxEqualIso(a: string, b: string): boolean {
  const timeA = Date.parse(a);
  const timeB = Date.parse(b);
  if (Number.isNaN(timeA) || Number.isNaN(timeB)) return a === b;
  return Math.abs(timeA - timeB) < 60000;
}

function toStringArray(rawValue: unknown): string[] {
  if (Array.isArray(rawValue)) return rawValue.map(String);
  return rawValue != null && rawValue !== "" ? [String(rawValue)] : [];
}

function toFirstString(rawValue: unknown): string[] {
  const single = Array.isArray(rawValue) ? rawValue[0] : rawValue;
  return single != null && single !== "" ? [String(single)] : [];
}

function resolveDurationUiValue(apiValue: unknown): string[] {
  if (
    typeof apiValue === "string" &&
    DURATION_OPTIONS.some((d) => d.key === apiValue)
  ) {
    return [apiValue];
  }

  if (Array.isArray(apiValue) && apiValue.length >= 2) {
    const start = String(apiValue[0]);
    const end = String(apiValue[1]);
    for (const option of DURATION_OPTIONS) {
      const range = getDurationUTCValue(option.key);
      if (
        range.length === 2 &&
        isApproxEqualIso(range[0], start) &&
        isApproxEqualIso(range[1], end)
      ) {
        return [option.key];
      }
    }
    return [start];
  }

  if (typeof apiValue === "string") return [apiValue];
  return [];
}

function getDefaultComparison(
  fieldKey: string,
  customFields?: HelpdeskCustomFieldDefinition[]
): string {
  if (ALL_NATIVE_FIELDS.includes(fieldKey)) {
    if (fieldKey === "request_support" || fieldKey === "duration")
      return "equals";
    return "contains";
  }
  const fieldType = customFields?.find((f) => f.key === fieldKey)?.type;
  return getDefaultOperatorForFieldType(fieldType);
}

function buildConditionUi(
  fieldKey: string,
  comparisonOperator: string,
  conditionValue: string[],
  isCustomField: boolean
): FilterConditionUI {
  return {
    key: fieldKey,
    comparison_operator: comparisonOperator,
    value: conditionValue,
    is_custom_field: isCustomField,
  };
}

function mapApiConditionToUi(
  apiCondition: FilterCondition,
  customFields?: HelpdeskCustomFieldDefinition[]
): FilterConditionUI {
  const fieldKey = (apiCondition?.key ?? "").trim();
  if (!fieldKey) return createEmptyCondition();

  const isCustomField = Boolean(apiCondition?.is_custom_field);
  const operator =
    apiCondition?.comparison_operator ||
    getDefaultComparison(fieldKey, customFields);
  const rawValue = apiCondition.value;

  if (operator === "exists") {
    const raw = rawValue as unknown;
    const boolVal = raw === true || raw === "true";
    return buildConditionUi(
      fieldKey,
      "exists",
      [String(boolVal)],
      isCustomField
    );
  }

  if (fieldKey === "request_support") {
    const raw = rawValue as unknown;
    const booleanValue =
      typeof raw === "boolean"
        ? raw
        : raw === "true" || raw === true || raw === 1 || raw === "1";
    return buildConditionUi(
      fieldKey,
      operator,
      [String(booleanValue)],
      isCustomField
    );
  }

  if (NATIVE_ARRAY_FIELDS.includes(fieldKey)) {
    return buildConditionUi(
      fieldKey,
      operator,
      toStringArray(rawValue),
      isCustomField
    );
  }

  if (NATIVE_STRING_FIELDS.includes(fieldKey)) {
    return buildConditionUi(
      fieldKey,
      operator,
      toFirstString(rawValue),
      isCustomField
    );
  }

  if (fieldKey === "duration") {
    if (
      operator === "range" &&
      Array.isArray(rawValue) &&
      rawValue.length >= 2
    ) {
      return buildConditionUi(
        fieldKey,
        "range",
        [String(rawValue[0]), String(rawValue[1])],
        isCustomField
      );
    }
    return buildConditionUi(
      fieldKey,
      operator,
      resolveDurationUiValue(rawValue),
      isCustomField
    );
  }

  const fieldType = customFields?.find((f) => f.key === fieldKey)?.type;

  if (fieldType === "dropdown_multi_select" || fieldType === "checkbox") {
    return buildConditionUi(fieldKey, operator, toStringArray(rawValue), true);
  }

  if (fieldType === "number") {
    if (operator === "range" && Array.isArray(rawValue)) {
      return buildConditionUi(
        fieldKey,
        "range",
        rawValue.map((n) => String(n)),
        true
      );
    }
    return buildConditionUi(fieldKey, operator, toFirstString(rawValue), true);
  }

  if (fieldType === "date") {
    if (
      operator === "range" &&
      Array.isArray(rawValue) &&
      rawValue.length >= 2
    ) {
      return buildConditionUi(
        fieldKey,
        "range",
        [String(rawValue[0]), String(rawValue[1])],
        true
      );
    }
    const dateStr = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    if (
      typeof dateStr === "string" &&
      DURATION_OPTIONS.some((d) => d.key === dateStr)
    ) {
      return buildConditionUi(fieldKey, operator, [dateStr], true);
    }
    const dateValue =
      dateStr != null && dateStr !== ""
        ? [String(dateStr)]
        : resolveDurationUiValue(rawValue);
    return buildConditionUi(fieldKey, operator, dateValue, true);
  }

  return buildConditionUi(
    fieldKey,
    operator,
    toFirstString(rawValue),
    isCustomField
  );
}

export function mapHelpdeskViewToFormValues(
  view: HelpdeskViewItem,
  customFields?: HelpdeskCustomFieldDefinition[]
): { values: ViewFormValues; emoji: string } {
  const rawFilterGroups = view.filter_groups as
    | {
        conditions?: FilterCondition[];
        logical_operator?: string;
      }[]
    | undefined;

  let filterGroups: FilterGroupUI[];

  if (rawFilterGroups?.length) {
    filterGroups = rawFilterGroups.map((group, groupIndex) => ({
      conditions: group?.conditions?.map((condition) =>
        mapApiConditionToUi(condition, customFields)
      ) ?? [createEmptyCondition()],
      logical_operator: group?.logical_operator === "or" ? "or" : "and",
    }));
  } else {
    filterGroups = [createEmptyGroup()];
  }

  const rawScope = view.scope;
  const scope =
    rawScope === "public" || rawScope === "private" ? rawScope : ("" as const);

  const values: ViewFormValues = {
    name: view.name ?? "",
    scope,
    filter_groups: filterGroups,
    logical_operator: view.logical_operator === "or" ? "or" : "and",
  };

  return {
    values,
    emoji: view.emoji_icon ?? "",
  };
}
